import { useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, Receipt, UserRound } from "lucide-react";
import { api, errorMessage, formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const kindLabel = { trial_start: "Trial started", subscription_charge: "Monthly charge", "subscription.charged": "Renewal", "subscription.activated": "Activated" };

function ProfileCard({ user, setUser }) {
  const [name, setName] = useState(user.name);
  const [saving, setSaving] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { const { data } = await api.patch("/account/profile", { name }); setUser(data); toast.success("Profile updated"); }
    catch (err) { toast.error(errorMessage(err)); } finally { setSaving(false); }
  };
  return (
    <form onSubmit={save} data-testid="account-profile-card" className="rounded-2xl border bg-card p-7 space-y-4">
      <p className="data-label flex items-center gap-2"><UserRound size={14} /> Profile</p>
      <div className="space-y-2"><Label htmlFor="name">Full name</Label><Input id="name" data-testid="account-name-input" value={name} onChange={(e) => setName(e.target.value)} minLength={2} required className="h-11 rounded-xl" /></div>
      <div className="space-y-2"><Label>Email</Label><Input value={user.email} disabled className="h-11 rounded-xl font-mono text-sm" /><p className="text-xs text-muted-foreground">Signed in via {user.auth_provider === "google" ? "Google" : "email & password"} · {user.email_verified ? "verified" : "not verified"}</p></div>
      <Button data-testid="account-save-profile-button" type="submit" disabled={saving || name === user.name} className="rounded-full bg-emerald-900 hover:bg-emerald-800 dark:bg-emerald-500 dark:text-slate-950">Save changes</Button>
    </form>
  );
}

function PasswordCard({ user, refreshUser }) {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm) return toast.error("Passwords do not match");
    setSaving(true);
    try {
      await api.post("/account/password", { current_password: form.current_password || null, new_password: form.new_password });
      toast.success(user.has_password ? "Password changed" : "Password set — you can now log in with email too");
      setForm({ current_password: "", new_password: "", confirm: "" });
      refreshUser();
    } catch (err) { toast.error(errorMessage(err)); } finally { setSaving(false); }
  };
  return (
    <form onSubmit={save} data-testid="account-password-card" className="rounded-2xl border bg-card p-7 space-y-4">
      <p className="data-label flex items-center gap-2"><KeyRound size={14} /> {user.has_password ? "Change password" : "Set a password"}</p>
      {!user.has_password && <p className="text-sm text-muted-foreground">Your account uses Google sign-in. Add a password to also log in with email.</p>}
      {user.has_password && <div className="space-y-2"><Label>Current password</Label><Input type="password" data-testid="account-current-password-input" value={form.current_password} onChange={set("current_password")} required className="h-11 rounded-xl" /></div>}
      <div className="space-y-2"><Label>New password</Label><Input type="password" data-testid="account-new-password-input" value={form.new_password} onChange={set("new_password")} required minLength={8} className="h-11 rounded-xl" /></div>
      <div className="space-y-2"><Label>Confirm new password</Label><Input type="password" data-testid="account-confirm-password-input" value={form.confirm} onChange={set("confirm")} required minLength={8} className="h-11 rounded-xl" /></div>
      <Button data-testid="account-save-password-button" type="submit" disabled={saving} className="rounded-full bg-emerald-900 hover:bg-emerald-800 dark:bg-emerald-500 dark:text-slate-950">{user.has_password ? "Update password" : "Set password"}</Button>
    </form>
  );
}

function PaymentsCard() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/account/payments").then(({ data }) => setRows(data)).catch(() => setRows([])); }, []);
  return (
    <div data-testid="account-payments-card" className="rounded-2xl border bg-card overflow-hidden">
      <div className="p-7 pb-4"><p className="data-label flex items-center gap-2"><Receipt size={14} /> Payment history</p></div>
      {rows && rows.length === 0 ? (
        <p data-testid="account-payments-empty" className="px-7 pb-7 text-sm text-muted-foreground">No payments yet.</p>
      ) : (
        <Table>
          <TableHeader><TableRow><TableHead className="pl-7">Date</TableHead><TableHead>Description</TableHead><TableHead>Provider</TableHead><TableHead className="text-right pr-7">Amount</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows?.map((p, i) => (
              <TableRow key={i} data-testid="account-payment-row">
                <TableCell className="pl-7 font-mono text-xs">{formatDate(p.created_at)}</TableCell>
                <TableCell>{kindLabel[p.kind] || p.kind}</TableCell>
                <TableCell className="text-xs uppercase text-muted-foreground">{p.provider}</TableCell>
                <TableCell className="text-right pr-7 font-mono">{p.amount_inr ? `₹${p.amount_inr}` : "₹0"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

export default function Account() {
  const { user, setUser, refreshUser } = useAuth();
  return (
    <div data-testid="account-page" className="max-w-5xl mx-auto px-6 py-12">
      <p className="data-label mb-3">Account</p>
      <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Settings</h1>
      <div className="mt-10 grid lg:grid-cols-2 gap-6">
        <ProfileCard user={user} setUser={setUser} />
        <PasswordCard user={user} refreshUser={refreshUser} />
        <div className="lg:col-span-2"><PaymentsCard /></div>
      </div>
    </div>
  );
}
